import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  Bell,
  Clock,
  CheckCircle,
  XCircle,
  HardDrive,
  Activity,
  HeartPulse,
  Info,
  Github,
  Eye,
  EyeOff,
  Trash2,
  Shield,
  Database,
  Download,
} from 'lucide-react';
import { api } from '../services/api';

export default function Settings() {
  const [activeSection, setActiveSection] = useState<
    'security' | 'sftp' | 'smtp' | 'notifications' | 'kuma' | 'github' | 'scheduler' | 'health' | 'retention' | 'info'
  >('security');

  const sections = [
    { key: 'security', label: 'Sicherheit', icon: Shield },
    { key: 'sftp', label: 'Storage Box (SFTP)', icon: HardDrive },
    { key: 'smtp', label: 'E-Mail (SMTP)', icon: Mail },
    { key: 'notifications', label: 'Benachrichtigungen', icon: Bell },
    { key: 'kuma', label: 'Uptime Kuma', icon: Activity },
    { key: 'github', label: 'GitHub', icon: Github },
    { key: 'scheduler', label: 'Scheduler', icon: Clock },
    { key: 'health', label: 'Health Check', icon: HeartPulse },
    { key: 'retention', label: 'Datenspeicherung', icon: Database },
    { key: 'info', label: 'System-Info', icon: Info },
  ] as const;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Einstellungen</h1>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="card p-4">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeSection === section.key
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <section.icon className="w-5 h-5" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="lg:col-span-3">
          {activeSection === 'security' && <SecuritySettings />}
          {activeSection === 'sftp' && <SftpSettings />}
          {activeSection === 'smtp' && <SmtpSettings />}
          {activeSection === 'notifications' && <NotificationSettings />}
          {activeSection === 'kuma' && <KumaSettings />}
          {activeSection === 'github' && <GithubSettings />}
          {activeSection === 'scheduler' && <SchedulerSettings />}
          {activeSection === 'health' && <HealthCheckSettings />}
          {activeSection === 'retention' && <RetentionSettings />}
          {activeSection === 'info' && <SystemInfo />}
        </div>
      </div>
    </div>
  );
}

function SecuritySettings() {
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const mutation = useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      api.auth.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setResult({ success: true, message: 'Passwort erfolgreich geändert' });
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    },
    onError: (err: Error) => {
      setResult({ success: false, message: err.message });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);

    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      setResult({ success: false, message: 'Bitte alle Felder ausfüllen' });
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setResult({ success: false, message: 'Die neuen Passwörter stimmen nicht überein' });
      return;
    }

    if (form.newPassword.length < 8) {
      setResult({ success: false, message: 'Das neue Passwort muss mindestens 8 Zeichen lang sein' });
      return;
    }

    mutation.mutate({
      currentPassword: form.currentPassword,
      newPassword: form.newPassword,
    });
  };

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5" />
        Passwort ändern
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Ändern Sie das Passwort für den Zugang zu BackupPilot.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <div>
          <label className="label">Aktuelles Passwort</label>
          <div className="relative">
            <input
              type={showPasswords.current ? 'text' : 'password'}
              value={form.currentPassword}
              onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
              className="input pr-10"
              placeholder="Aktuelles Passwort eingeben"
            />
            <button
              type="button"
              onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div>
          <label className="label">Neues Passwort</label>
          <div className="relative">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              value={form.newPassword}
              onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
              className="input pr-10"
              placeholder="Neues Passwort (mind. 8 Zeichen)"
            />
            <button
              type="button"
              onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div>
          <label className="label">Neues Passwort bestätigen</label>
          <div className="relative">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
              className="input pr-10"
              placeholder="Neues Passwort wiederholen"
            />
            <button
              type="button"
              onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <TestResultBanner result={result} />

        <button
          type="submit"
          disabled={mutation.isPending}
          className="btn btn-primary"
        >
          {mutation.isPending ? 'Ändere...' : 'Passwort ändern'}
        </button>
      </form>
    </div>
  );
}

function SftpSettings() {
  const queryClient = useQueryClient();
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  const { data: config, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: api.settings.getConfig,
  });

  const [form, setForm] = useState({
    host: '',
    port: '23',
    user: '',
    newPassword: '',
    newPrivateKey: '',
    basePath: '/backups',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, string | number | boolean>) =>
      api.settings.updateConfig('sftp', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config'] });
      setTestResult({ success: true, message: 'Konfiguration gespeichert' });
      setShowKeyInput(false);
      setShowPasswordInput(false);
      setForm(f => ({ ...f, newPassword: '', newPrivateKey: '' }));
    },
    onError: (err: Error) => {
      setTestResult({ success: false, message: err.message });
    },
  });

  const testMutation = useMutation({
    mutationFn: api.settings.testSftp,
    onSuccess: (result) => setTestResult(result),
    onError: (err: Error) =>
      setTestResult({ success: false, message: err.message }),
  });

  // Update form when config loads
  if (config && form.host === '' && config.sftp.host) {
    setForm(f => ({
      ...f,
      host: config.sftp.host,
      port: config.sftp.port.toString(),
      user: config.sftp.user,
      basePath: config.sftp.basePath,
    }));
  }

  if (isLoading) {
    return <LoadingCard />;
  }

  const hasPassword = config?.sftp.password === 'SET';
  const hasPrivateKey = config?.sftp.privateKey === 'SET';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: Record<string, string | number | boolean> = {
      host: form.host,
      port: parseInt(form.port, 10),
      user: form.user,
      basePath: form.basePath,
    };

    // Nur senden wenn wirklich ein neuer Wert eingegeben wurde
    if (form.newPassword && form.newPassword.trim() !== '') {
      data.password = form.newPassword;
    }
    if (form.newPrivateKey && form.newPrivateKey.trim() !== '') {
      data.privateKey = form.newPrivateKey;
    }

    mutation.mutate(data);
  };

  const handleClearPassword = () => {
    mutation.mutate({ password: '' });
  };

  const handleClearPrivateKey = () => {
    mutation.mutate({ privateKey: '' });
  };

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <HardDrive className="w-5 h-5" />
        Storage Box (SFTP) Konfiguration
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Verbindungsdaten für die Hetzner Storage Box. SSH-Key wird bevorzugt, alternativ Passwort.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Host</label>
            <input
              type="text"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              className="input"
              placeholder="u123456.your-storagebox.de"
            />
          </div>
          <div>
            <label className="label">Port</label>
            <input
              type="number"
              value={form.port}
              onChange={(e) => setForm({ ...form, port: e.target.value })}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">Hetzner Storage Box: 23</p>
          </div>
        </div>

        <div>
          <label className="label">Benutzer</label>
          <input
            type="text"
            value={form.user}
            onChange={(e) => setForm({ ...form, user: e.target.value })}
            className="input"
            placeholder="u123456"
          />
        </div>

        {/* SSH Private Key */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <label className="label mb-0">SSH Private Key (empfohlen)</label>
            <div className="flex items-center gap-2">
              {hasPrivateKey && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Key hinterlegt
                </span>
              )}
            </div>
          </div>

          {!showKeyInput ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowKeyInput(true)}
                className="btn btn-secondary text-sm"
              >
                {hasPrivateKey ? 'Key ersetzen' : 'Key hinzufügen'}
              </button>
              {hasPrivateKey && (
                <button
                  type="button"
                  onClick={handleClearPrivateKey}
                  className="btn text-sm text-red-600 hover:bg-red-50"
                >
                  Key entfernen
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={form.newPrivateKey}
                onChange={(e) => setForm({ ...form, newPrivateKey: e.target.value })}
                className="input font-mono text-xs"
                rows={6}
                placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;...&#10;-----END OPENSSH PRIVATE KEY-----"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowKeyInput(false);
                    setForm(f => ({ ...f, newPrivateKey: '' }));
                  }}
                  className="btn btn-secondary text-sm"
                >
                  Abbrechen
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Der Key wird erst beim Klick auf "Speichern" gespeichert.
              </p>
            </div>
          )}
        </div>

        {/* Password (Alternative) */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="label mb-0">Passwort (Alternative)</label>
            {hasPassword && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Passwort hinterlegt
              </span>
            )}
          </div>

          {!showPasswordInput ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowPasswordInput(true)}
                className="btn btn-secondary text-sm"
              >
                {hasPassword ? 'Passwort ändern' : 'Passwort setzen'}
              </button>
              {hasPassword && (
                <button
                  type="button"
                  onClick={handleClearPassword}
                  className="btn text-sm text-red-600 hover:bg-red-50"
                >
                  Passwort entfernen
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <input
                type="password"
                value={form.newPassword}
                onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                className="input"
                placeholder="Neues Passwort"
              />
              <button
                type="button"
                onClick={() => {
                  setShowPasswordInput(false);
                  setForm(f => ({ ...f, newPassword: '' }));
                }}
                className="btn btn-secondary text-sm"
              >
                Abbrechen
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="label">Basis-Pfad</label>
          <input
            type="text"
            value={form.basePath}
            onChange={(e) => setForm({ ...form, basePath: e.target.value })}
            className="input"
            placeholder="/backups"
          />
          <p className="text-xs text-gray-500 mt-1">
            Wird nur benötigt wenn BackupPilot selbst Dateien suchen soll
          </p>
        </div>

        <TestResultBanner result={testResult} />

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn btn-primary"
          >
            {mutation.isPending ? 'Speichern...' : 'Speichern'}
          </button>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending}
            className="btn btn-secondary"
          >
            {testMutation.isPending ? 'Teste...' : 'Verbindung testen'}
          </button>
        </div>
      </form>
    </div>
  );
}

function SmtpSettings() {
  const queryClient = useQueryClient();
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [testEmail, setTestEmail] = useState('');

  const { data: config, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: api.settings.getConfig,
  });

  const [form, setForm] = useState({
    host: '',
    port: '587',
    secure: false,
    user: '',
    password: '',
    from: '',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, string | number | boolean>) =>
      api.settings.updateConfig('smtp', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config'] });
      setTestResult({ success: true, message: 'Konfiguration gespeichert' });
    },
    onError: (err: Error) => {
      setTestResult({ success: false, message: err.message });
    },
  });

  const testMutation = useMutation({
    mutationFn: api.settings.testSmtp,
    onSuccess: (result) => setTestResult(result),
    onError: (err: Error) =>
      setTestResult({ success: false, message: err.message }),
  });

  const sendTestMutation = useMutation({
    mutationFn: api.settings.testEmail,
    onSuccess: (result) => setTestResult(result),
    onError: (err: Error) =>
      setTestResult({ success: false, message: err.message }),
  });

  if (config && form.host === '' && config.smtp.host) {
    setForm({
      host: config.smtp.host,
      port: config.smtp.port.toString(),
      secure: config.smtp.secure,
      user: config.smtp.user,
      password: config.smtp.password,
      from: config.smtp.from,
    });
  }

  if (isLoading) {
    return <LoadingCard />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      host: form.host,
      port: parseInt(form.port, 10),
      secure: form.secure,
      user: form.user,
      password: form.password === '********' ? '' : form.password,
      from: form.from,
    });
  };

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Mail className="w-5 h-5" />
        E-Mail (SMTP) Konfiguration
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        SMTP-Server für den Versand von Benachrichtigungen.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">SMTP Host</label>
            <input
              type="text"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              className="input"
              placeholder="smtp.example.com"
            />
          </div>
          <div>
            <label className="label">Port</label>
            <input
              type="number"
              value={form.port}
              onChange={(e) => setForm({ ...form, port: e.target.value })}
              className="input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Benutzer</label>
            <input
              type="text"
              value={form.user}
              onChange={(e) => setForm({ ...form, user: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="label">Passwort</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="input"
              placeholder="********"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Absender E-Mail</label>
            <input
              type="email"
              value={form.from}
              onChange={(e) => setForm({ ...form, from: e.target.value })}
              className="input"
              placeholder="backup@example.com"
            />
          </div>
          <div className="flex items-center">
            <label className="flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                checked={form.secure}
                onChange={(e) => setForm({ ...form, secure: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <span>SSL/TLS verwenden</span>
            </label>
          </div>
        </div>

        <div className="border-t pt-4">
          <label className="label">Test E-Mail senden</label>
          <div className="flex gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="input flex-1"
              placeholder="test@example.com"
            />
            <button
              type="button"
              onClick={() => sendTestMutation.mutate(testEmail)}
              disabled={!testEmail || sendTestMutation.isPending}
              className="btn btn-secondary"
            >
              Senden
            </button>
          </div>
        </div>

        <TestResultBanner result={testResult} />

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn btn-primary"
          >
            {mutation.isPending ? 'Speichern...' : 'Speichern'}
          </button>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending}
            className="btn btn-secondary"
          >
            {testMutation.isPending ? 'Teste...' : 'Verbindung testen'}
          </button>
        </div>
      </form>
    </div>
  );
}

function NotificationSettings() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: api.settings.getNotifications,
  });

  const mutation = useMutation({
    mutationFn: api.settings.updateNotifications,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
    },
  });

  if (isLoading) {
    return <LoadingCard />;
  }

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Bell className="w-5 h-5" />
        Benachrichtigungsoptionen
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Legen Sie fest, wann Sie per E-Mail benachrichtigt werden möchten.
      </p>

      <div className="space-y-4">
        <div>
          <label className="label">E-Mail Adresse für Benachrichtigungen</label>
          <input
            type="email"
            value={settings?.email || ''}
            onChange={(e) => mutation.mutate({ email: e.target.value })}
            className="input"
            placeholder="admin@example.com"
          />
        </div>

        <div className="space-y-3 pt-4">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings?.onBackupFailed || false}
              onChange={(e) =>
                mutation.mutate({ onBackupFailed: e.target.checked })
              }
              className="w-4 h-4 rounded border-gray-300 text-primary-600"
            />
            <div>
              <span className="font-medium">Backup fehlgeschlagen</span>
              <p className="text-sm text-gray-500">
                Sofortige Benachrichtigung wenn ein Backup fehlschlägt
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings?.onValidatorFailed || false}
              onChange={(e) =>
                mutation.mutate({ onValidatorFailed: e.target.checked })
              }
              className="w-4 h-4 rounded border-gray-300 text-primary-600"
            />
            <div>
              <span className="font-medium">Validierung fehlgeschlagen</span>
              <p className="text-sm text-gray-500">
                Benachrichtigung wenn die Backup-Validierung fehlschlägt
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings?.onHealthWarning || false}
              onChange={(e) =>
                mutation.mutate({ onHealthWarning: e.target.checked })
              }
              className="w-4 h-4 rounded border-gray-300 text-primary-600"
            />
            <div>
              <span className="font-medium">Health-Warnungen</span>
              <p className="text-sm text-gray-500">
                Benachrichtigung bei veralteten WordPress/PHP-Versionen
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings?.weeklyDigest || false}
              onChange={(e) =>
                mutation.mutate({ weeklyDigest: e.target.checked })
              }
              className="w-4 h-4 rounded border-gray-300 text-primary-600"
            />
            <div>
              <span className="font-medium">Wöchentliche Zusammenfassung</span>
              <p className="text-sm text-gray-500">
                Wöchentlicher Bericht über alle Backup-Aktivitäten
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}

function KumaSettings() {
  const queryClient = useQueryClient();
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  const { data: config, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: api.settings.getConfig,
  });

  const [form, setForm] = useState({
    url: '',
    username: '',
    newPassword: '',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, string | number | boolean>) =>
      api.settings.updateConfig('kuma', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config'] });
      setTestResult({ success: true, message: 'Konfiguration gespeichert' });
      setShowPasswordInput(false);
      setForm(f => ({ ...f, newPassword: '' }));
    },
    onError: (err: Error) => {
      setTestResult({ success: false, message: err.message });
    },
  });

  const testMutation = useMutation({
    mutationFn: api.settings.testKuma,
    onSuccess: (result) => setTestResult(result),
    onError: (err: Error) =>
      setTestResult({ success: false, message: err.message }),
  });

  if (config && form.url === '' && config.kuma.url) {
    setForm(f => ({
      ...f,
      url: config.kuma.url,
      username: config.kuma.username,
    }));
  }

  if (isLoading) {
    return <LoadingCard />;
  }

  const hasPassword = config?.kuma.password === 'SET';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: Record<string, string | number | boolean> = {
      url: form.url,
      username: form.username,
    };

    // Nur senden wenn wirklich ein neues Passwort eingegeben wurde
    if (form.newPassword && form.newPassword.trim() !== '') {
      data.password = form.newPassword;
    }

    mutation.mutate(data);
  };

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5" />
        Uptime Kuma Integration
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Verbindung zu Uptime Kuma via Socket.IO. Verwende deine Kuma Login-Daten.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Uptime Kuma URL</label>
          <input
            type="url"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="input"
            placeholder="https://kuma.example.com"
          />
        </div>

        <div>
          <label className="label">Username</label>
          <input
            type="text"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            className="input"
            placeholder="Dein Kuma Username"
          />
        </div>

        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="label mb-0">Passwort</label>
            {hasPassword && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Passwort hinterlegt
              </span>
            )}
          </div>

          {!showPasswordInput ? (
            <button
              type="button"
              onClick={() => setShowPasswordInput(true)}
              className="btn btn-secondary text-sm"
            >
              {hasPassword ? 'Passwort ändern' : 'Passwort setzen'}
            </button>
          ) : (
            <div className="space-y-2">
              <input
                type="password"
                value={form.newPassword}
                onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                className="input"
                placeholder="Neues Passwort"
              />
              <button
                type="button"
                onClick={() => {
                  setShowPasswordInput(false);
                  setForm(f => ({ ...f, newPassword: '' }));
                }}
                className="btn btn-secondary text-sm"
              >
                Abbrechen
              </button>
            </div>
          )}
        </div>

        <TestResultBanner result={testResult} />

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn btn-primary"
          >
            {mutation.isPending ? 'Speichern...' : 'Speichern'}
          </button>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending || !form.url || !form.username}
            className="btn btn-secondary"
          >
            {testMutation.isPending ? 'Teste...' : 'Verbindung testen'}
          </button>
        </div>
      </form>
    </div>
  );
}

function GithubSettings() {
  const queryClient = useQueryClient();
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [newToken, setNewToken] = useState('');

  const { data: githubSettings, isLoading } = useQuery({
    queryKey: ['github-settings'],
    queryFn: api.settings.getGithubSettings,
  });

  const saveMutation = useMutation({
    mutationFn: api.settings.updateGithubToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github-settings'] });
      setTestResult({ success: true, message: 'Token gespeichert' });
      setShowTokenInput(false);
      setNewToken('');
    },
    onError: (err: Error) => {
      setTestResult({ success: false, message: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.settings.deleteGithubToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github-settings'] });
      setTestResult({ success: true, message: 'Token entfernt' });
    },
    onError: (err: Error) => {
      setTestResult({ success: false, message: err.message });
    },
  });

  const testMutation = useMutation({
    mutationFn: api.settings.testGithubToken,
    onSuccess: (result) => setTestResult(result),
    onError: (err: Error) =>
      setTestResult({ success: false, message: err.message }),
  });

  if (isLoading) {
    return <LoadingCard />;
  }

  const hasToken = githubSettings?.tokenConfigured;

  const handleSaveToken = () => {
    if (newToken.trim()) {
      saveMutation.mutate(newToken.trim());
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Github className="w-5 h-5" />
        GitHub Integration
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Personal Access Token für den Zugriff auf private GitHub Repositories.
        Erforderlich für die Plugin-Synchronisation mit privaten Repos.
      </p>

      <div className="space-y-4">
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="label mb-0">Personal Access Token (PAT)</label>
            {hasToken && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Token konfiguriert
              </span>
            )}
          </div>

          {!showTokenInput ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowTokenInput(true)}
                className="btn btn-secondary text-sm"
              >
                {hasToken ? 'Token ändern' : 'Token hinzufügen'}
              </button>
              {hasToken && (
                <>
                  <button
                    type="button"
                    onClick={() => testMutation.mutate()}
                    disabled={testMutation.isPending}
                    className="btn btn-secondary text-sm"
                  >
                    {testMutation.isPending ? 'Teste...' : 'Token testen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="btn text-sm text-red-600 hover:bg-red-50 flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Entfernen
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={newToken}
                  onChange={(e) => setNewToken(e.target.value)}
                  className="input pr-10 font-mono text-sm"
                  placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showToken ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveToken}
                  disabled={!newToken.trim() || saveMutation.isPending}
                  className="btn btn-primary text-sm"
                >
                  {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowTokenInput(false);
                    setNewToken('');
                    setShowToken(false);
                  }}
                  className="btn btn-secondary text-sm"
                >
                  Abbrechen
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Erstelle einen Personal Access Token unter{' '}
                <a
                  href="https://github.com/settings/tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  GitHub Settings → Developer settings → Personal access tokens
                </a>
                . Berechtigungen: <code className="bg-gray-100 px-1 rounded">repo</code> (für private Repos) oder{' '}
                <code className="bg-gray-100 px-1 rounded">public_repo</code> (nur für öffentliche Repos).
              </p>
            </div>
          )}
        </div>

        <TestResultBanner result={testResult} />

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Hinweis</h4>
          <p className="text-sm text-blue-800">
            Der Token wird verschlüsselt in der Datenbank gespeichert und nur für API-Anfragen an GitHub verwendet.
            Fine-grained tokens werden empfohlen und sollten nur Zugriff auf die benotigten Repositories haben.
          </p>
        </div>
      </div>
    </div>
  );
}

function SchedulerSettings() {
  const queryClient = useQueryClient();
  const [runResult, setRunResult] = useState<{
    task: string;
    success: boolean;
    message: string;
  } | null>(null);

  const { data: tasks, isLoading } = useQuery({
    queryKey: ['scheduler-tasks'],
    queryFn: api.settings.getScheduler,
  });

  const runMutation = useMutation({
    mutationFn: (task: string) => api.settings.runTask(task),
    onSuccess: (result, task) => {
      setRunResult({ task, ...result });
      queryClient.invalidateQueries({ queryKey: ['scheduler-tasks'] });
    },
    onError: (err: Error, task) => {
      setRunResult({ task, success: false, message: err.message });
    },
  });

  if (isLoading) {
    return <LoadingCard />;
  }

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5" />
        Geplante Aufgaben
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Übersicht und manuelle Ausführung der geplanten Hintergrundaufgaben.
      </p>

      {runResult && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${
            runResult.success
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {runResult.success ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          {runResult.message}
        </div>
      )}

      <div className="space-y-4">
        {tasks?.map((task) => (
          <div
            key={task.name}
            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
          >
            <div>
              <p className="font-medium">{task.name}</p>
              <p className="text-sm text-gray-500">Schedule: {task.schedule}</p>
              {task.lastRun && (
                <p className="text-sm text-gray-500">
                  Letzter Lauf: {new Date(task.lastRun).toLocaleString('de-DE')}
                </p>
              )}
            </div>
            <button
              onClick={() => {
                const taskKey = task.name.toLowerCase().replace(' ', '-');
                runMutation.mutate(taskKey);
              }}
              disabled={runMutation.isPending}
              className="btn btn-secondary"
            >
              Jetzt ausführen
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HealthCheckSettings() {
  const queryClient = useQueryClient();
  const [saveResult, setSaveResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: api.settings.getConfig,
  });

  const [form, setForm] = useState({
    phpMinVersion: '8.0.0',
    wpMajorVersionThreshold: '2',
    pluginUpdatesWarning: '5',
    themeUpdatesWarning: '2',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, string | number | boolean>) =>
      api.settings.updateConfig('health', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config'] });
      setSaveResult({ success: true, message: 'Einstellungen gespeichert' });
    },
    onError: (err: Error) => {
      setSaveResult({ success: false, message: err.message });
    },
  });

  // Update form when config loads
  if (config?.health && form.phpMinVersion === '8.0.0' && config.health.phpMinVersion !== '8.0.0') {
    setForm({
      phpMinVersion: config.health.phpMinVersion,
      wpMajorVersionThreshold: config.health.wpMajorVersionThreshold.toString(),
      pluginUpdatesWarning: config.health.pluginUpdatesWarning.toString(),
      themeUpdatesWarning: config.health.themeUpdatesWarning.toString(),
    });
  }

  if (isLoading) {
    return <LoadingCard />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      phpMinVersion: form.phpMinVersion,
      wpMajorVersionThreshold: parseInt(form.wpMajorVersionThreshold, 10),
      pluginUpdatesWarning: parseInt(form.pluginUpdatesWarning, 10),
      themeUpdatesWarning: parseInt(form.themeUpdatesWarning, 10),
    });
  };

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <HeartPulse className="w-5 h-5" />
        Health Check Schwellenwerte
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Konfigurieren Sie die Schwellenwerte, ab denen ein Projekt als "Warnung" markiert wird.
        Diese Werte können angepasst werden, wenn sich Standards ändern (z.B. neue PHP-Versionen).
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">PHP Mindestversion</label>
            <input
              type="text"
              value={form.phpMinVersion}
              onChange={(e) => setForm({ ...form, phpMinVersion: e.target.value })}
              className="input"
              placeholder="8.0.0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Warnung wenn PHP-Version kleiner als dieser Wert
            </p>
          </div>
          <div>
            <label className="label">WordPress Major-Versionen</label>
            <input
              type="number"
              value={form.wpMajorVersionThreshold}
              onChange={(e) => setForm({ ...form, wpMajorVersionThreshold: e.target.value })}
              className="input"
              min="1"
              max="10"
            />
            <p className="text-xs text-gray-500 mt-1">
              Warnung wenn WordPress so viele Major-Versionen zurück liegt
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Plugin-Updates Warnung</label>
            <input
              type="number"
              value={form.pluginUpdatesWarning}
              onChange={(e) => setForm({ ...form, pluginUpdatesWarning: e.target.value })}
              className="input"
              min="1"
              max="50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Warnung wenn mehr als diese Anzahl Plugin-Updates ausstehen
            </p>
          </div>
          <div>
            <label className="label">Theme-Updates Warnung</label>
            <input
              type="number"
              value={form.themeUpdatesWarning}
              onChange={(e) => setForm({ ...form, themeUpdatesWarning: e.target.value })}
              className="input"
              min="1"
              max="20"
            />
            <p className="text-xs text-gray-500 mt-1">
              Warnung wenn mehr als diese Anzahl Theme-Updates ausstehen
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Aktuelle Schwellenwerte</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• PHP &lt; {config?.health?.phpMinVersion || '8.0.0'} → Warnung</li>
            <li>• WordPress {config?.health?.wpMajorVersionThreshold || 2}+ Major-Versionen veraltet → Warnung</li>
            <li>• Mehr als {config?.health?.pluginUpdatesWarning || 5} Plugin-Updates → Warnung</li>
            <li>• Mehr als {config?.health?.themeUpdatesWarning || 2} Theme-Updates → Warnung</li>
          </ul>
        </div>

        <TestResultBanner result={saveResult} />

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn btn-primary"
          >
            {mutation.isPending ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </form>
    </div>
  );
}

function RetentionSettings() {
  const queryClient = useQueryClient();
  const [saveResult, setSaveResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: api.settings.getConfig,
  });

  const [form, setForm] = useState({
    logDays: '90',
    healthCheckDays: '30',
    screenshotRuns: '10',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, string | number | boolean>) =>
      api.settings.updateConfig('retention', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config'] });
      setSaveResult({ success: true, message: 'Einstellungen gespeichert' });
    },
    onError: (err: Error) => {
      setSaveResult({ success: false, message: err.message });
    },
  });

  // Update form when config loads
  if (config?.retention && form.logDays === '90' && config.retention.logDays !== 90) {
    setForm({
      logDays: config.retention.logDays.toString(),
      healthCheckDays: config.retention.healthCheckDays.toString(),
      screenshotRuns: config.retention.screenshotRuns.toString(),
    });
  }

  if (isLoading) {
    return <LoadingCard />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      logDays: parseInt(form.logDays, 10),
      healthCheckDays: parseInt(form.healthCheckDays, 10),
      screenshotRuns: parseInt(form.screenshotRuns, 10),
    });
  };

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Database className="w-5 h-5" />
          Aufbewahrungsfristen
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Legen Sie fest, wie lange verschiedene Daten aufbewahrt werden sollen.
          Ältere Daten werden automatisch gelöscht.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Logs (Tage)</label>
              <input
                type="number"
                value={form.logDays}
                onChange={(e) => setForm({ ...form, logDays: e.target.value })}
                className="input"
                min="7"
                max="365"
              />
              <p className="text-xs text-gray-500 mt-1">
                Aktivitäts- und Fehler-Logs
              </p>
            </div>
            <div>
              <label className="label">Health-Checks (Tage)</label>
              <input
                type="number"
                value={form.healthCheckDays}
                onChange={(e) => setForm({ ...form, healthCheckDays: e.target.value })}
                className="input"
                min="7"
                max="365"
              />
              <p className="text-xs text-gray-500 mt-1">
                Detaillierte Health-Check-Ergebnisse
              </p>
            </div>
            <div>
              <label className="label">Screenshot-Durchläufe</label>
              <input
                type="number"
                value={form.screenshotRuns}
                onChange={(e) => setForm({ ...form, screenshotRuns: e.target.value })}
                className="input"
                min="1"
                max="50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Anzahl der Validator-Durchläufe pro Projekt
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Aktuelle Einstellungen</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Logs werden nach {config?.retention?.logDays || 90} Tagen gelöscht</li>
              <li>• Health-Checks werden nach {config?.retention?.healthCheckDays || 30} Tagen gelöscht</li>
              <li>• Pro Projekt werden die letzten {config?.retention?.screenshotRuns || 10} Validator-Durchläufe aufbewahrt</li>
            </ul>
          </div>

          <TestResultBanner result={saveResult} />

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn btn-primary"
            >
              {mutation.isPending ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>

      {/* Data Export */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Download className="w-5 h-5" />
          Daten-Export
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Exportieren Sie Logs und andere Daten im CSV-Format.
        </p>

        <div className="space-y-3">
          <a
            href={api.logs.getExportUrl()}
            download
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Alle Logs exportieren (CSV)
          </a>
          <p className="text-xs text-gray-500">
            Der Export enthält alle Aktivitäts- und Fehler-Logs im CSV-Format.
          </p>
        </div>
      </div>
    </div>
  );
}

function SystemInfo() {
  const [showChangelog, setShowChangelog] = useState(false);
  const { data: versionInfo, isLoading } = useQuery({
    queryKey: ['version'],
    queryFn: api.settings.getVersion,
    staleTime: Infinity,
    retry: false,
  });

  const frontendVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
  const frontendCommit = typeof __GIT_COMMIT__ !== 'undefined' ? __GIT_COMMIT__ : 'unknown';
  const frontendBuildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : null;

  if (isLoading) {
    return <LoadingCard />;
  }

  // Simple markdown-to-html for changelog
  const formatChangelog = (text: string) => {
    return text
      .split('\n')
      .map((line, i) => {
        // Headers
        if (line.startsWith('# ')) {
          return <h1 key={i} className="text-xl font-bold mt-4 mb-2">{line.slice(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={i} className="text-lg font-semibold mt-6 mb-2 text-primary-700 border-b pb-1">{line.slice(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={i} className="text-md font-medium mt-4 mb-1 text-gray-800">{line.slice(4)}</h3>;
        }
        // List items
        if (line.startsWith('- **')) {
          const match = line.match(/^- \*\*(.+?)\*\*:?\s*(.*)$/);
          if (match) {
            return (
              <li key={i} className="ml-4 mb-1">
                <span className="font-semibold">{match[1]}</span>
                {match[2] && <span className="text-gray-600">: {match[2]}</span>}
              </li>
            );
          }
        }
        if (line.startsWith('- ')) {
          return <li key={i} className="ml-4 text-gray-700">{line.slice(2)}</li>;
        }
        if (line.startsWith('  - ')) {
          return <li key={i} className="ml-8 text-gray-600 text-sm">{line.slice(4)}</li>;
        }
        // Empty lines
        if (line.trim() === '') {
          return <div key={i} className="h-2" />;
        }
        // Regular text
        return <p key={i} className="text-gray-600 text-sm">{line}</p>;
      });
  };

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Info className="w-5 h-5" />
          System-Informationen
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Versionsinformationen und Git-Commits von Frontend und Backend.
        </p>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Frontend</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block">Version</span>
                <span className="font-mono font-medium">v{frontendVersion}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Commit</span>
                <span className="font-mono text-blue-600">{frontendCommit}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Build</span>
                <span className="font-mono text-gray-600">
                  {frontendBuildTime
                    ? new Date(frontendBuildTime).toLocaleString('de-DE')
                    : '-'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Backend</h3>
            {versionInfo?.backend ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 block">Version</span>
                  <span className="font-mono font-medium">v{versionInfo.backend.version}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Commit</span>
                  <span className="font-mono text-green-600">{versionInfo.backend.commit}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Build</span>
                  <span className="font-mono text-gray-600">
                    {versionInfo.backend.buildTime
                      ? new Date(versionInfo.backend.buildTime).toLocaleString('de-DE')
                      : '-'}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Backend-Version nicht verfügbar</p>
            )}
          </div>
        </div>
      </div>

      {/* Changelog */}
      <div className="card p-6">
        <button
          onClick={() => setShowChangelog(!showChangelog)}
          className="w-full flex items-center justify-between text-left"
        >
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Changelog
          </h2>
          <span className="text-gray-400 text-sm">
            {showChangelog ? '▲ Einklappen' : '▼ Ausklappen'}
          </span>
        </button>

        {showChangelog && versionInfo?.backend?.changelog && (
          <div className="mt-4 pt-4 border-t max-h-96 overflow-y-auto">
            <div className="prose prose-sm max-w-none">
              {formatChangelog(versionInfo.backend.changelog)}
            </div>
          </div>
        )}

        {showChangelog && !versionInfo?.backend?.changelog && (
          <div className="mt-4 pt-4 border-t text-gray-500 text-sm">
            Kein Changelog verfügbar.
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingCard() {
  return (
    <div className="card p-6 flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>
  );
}

function TestResultBanner({
  result,
}: {
  result: { success: boolean; message: string } | null;
}) {
  if (!result) return null;

  return (
    <div
      className={`flex items-center gap-2 p-3 rounded-lg ${
        result.success
          ? 'bg-green-50 text-green-700'
          : 'bg-red-50 text-red-700'
      }`}
    >
      {result.success ? (
        <CheckCircle className="w-5 h-5" />
      ) : (
        <XCircle className="w-5 h-5" />
      )}
      {result.message}
    </div>
  );
}
